import React, { useCallback, useEffect, useState } from 'react';
import b2bService from '../../services/b2bService';

/**
 * Keys that let one client's own system send orders (administrators only; the server refuses everyone else).
 * A key is shown once, when it is made or replaced: only a hash is stored, so it cannot be looked up later.
 */
const B2bApiKeysPanel = ({ partyId }) => {
  const [clients, setClients] = useState([]);
  const [name, setName] = useState('');
  const [ips, setIps] = useState('');
  const [shown, setShown] = useState(null);       // { name, key } for the one-time display
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!partyId) return;
    try {
      setClients(await b2bService.getApiClients(partyId));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the keys.');
    }
  }, [partyId]);

  useEffect(() => { setShown(null); load(); }, [load]);

  const run = async (action) => {
    setBusy(true);
    setError('');
    try { await action(); } catch (err) { setError(err.response?.data?.message || 'The request failed.'); } finally { setBusy(false); load(); }
  };

  const create = (e) => {
    e.preventDefault();
    return run(async () => {
      const res = await b2bService.createApiClient({ partyId: Number(partyId), clientName: name.trim(), whitelistedIps: ips.trim() || null });
      setShown({ name: res.client.clientName, key: res.apiKey });
      setName('');
      setIps('');
    });
  };

  const rotate = (c) => {
    if (!window.confirm(`Replace the key of "${c.clientName}"? The old key stops working at once.`)) return undefined;
    return run(async () => { const res = await b2bService.rotateApiClient(c.credentialID); setShown({ name: c.clientName, key: res.apiKey }); });
  };

  const revoke = (c) => {
    if (!window.confirm(`Switch off the key of "${c.clientName}"?`)) return undefined;
    return run(() => b2bService.revokeApiClient(c.credentialID));
  };

  return (
    <div className="b2b-table-card">
      {shown && (
        <div role="alert" style={{ padding: 16, background: '#fffbeb', borderBottom: '1px solid #fcd34d' }}>
          <strong>Key for {shown.name}. Copy it now: it cannot be shown again.</strong>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input className="b2b-filter-input" readOnly value={shown.key} aria-label="New API key" style={{ flex: 1, fontFamily: 'monospace' }} onFocus={(e) => e.target.select()} />
            <button type="button" className="b2b-refresh-btn" onClick={() => navigator.clipboard?.writeText(shown.key)}>Copy</button>
            <button type="button" className="b2b-refresh-btn" onClick={() => setShown(null)}>I have saved it</button>
          </div>
          <div style={{ fontSize: 12, marginTop: 6 }}>The client sends it in the <code>X-API-KEY</code> header of every order.</div>
        </div>
      )}

      <form onSubmit={create} className="b2b-filter-bar" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="b2b-filter-group">
          <label className="b2b-filter-label" htmlFor="apikey-name">Client system name</label>
          <input id="apikey-name" className="b2b-filter-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} required />
        </div>
        <div className="b2b-filter-group">
          <label className="b2b-filter-label" htmlFor="apikey-ips">Allowed IP addresses (optional, comma separated)</label>
          <input id="apikey-ips" className="b2b-filter-input" value={ips} onChange={(e) => setIps(e.target.value)} maxLength={500} placeholder="empty: any address" />
        </div>
        <button type="submit" className="b2b-refresh-btn" style={{ background: '#2563eb', color: '#fff', alignSelf: 'flex-end' }} disabled={busy || !name.trim()}>Create key</button>
      </form>

      {error && <div role="alert" style={{ padding: '10px 16px', color: '#b91c1c', fontWeight: 600 }}>{error}</div>}

      {clients.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No keys for this client yet.</div>
      ) : (
        <table className="b2b-data-table">
          <thead><tr><th>Client system</th><th>Allowed addresses</th><th>Created</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.credentialID}>
                <td><strong>{c.clientName}</strong></td>
                <td>{c.whitelistedIps || 'any'}</td>
                <td>{new Date(c.createdDate).toLocaleDateString('en-IN')}</td>
                <td><span className={`b2b-badge ${c.isActive ? 'completed' : 'failed'}`}>{c.isActive ? 'Active' : 'Off'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="b2b-refresh-btn" disabled={busy} onClick={() => rotate(c)}>Replace key</button>
                    {c.isActive && <button type="button" className="b2b-refresh-btn" disabled={busy} onClick={() => revoke(c)}>Switch off</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default B2bApiKeysPanel;
